export type DismissalRecord = {
  id: string
  scope: string
  dismissedAt: number
  expiresAt?: number
  version: 1
}

export type DismissOptions = {
  ttlMs?: number
  ttlDays?: number
  expiresAt?: number | Date
}

const NAMESPACE = 'tt:dismissal'
export const DISMISSALS_CHANGED_EVENT = 'tt:dismissals:changed'

const hasLocalStorage = (() => {
  try {
    const k = '__tt_probe__'
    window.localStorage.setItem(k, '1')
    window.localStorage.removeItem(k)
    return true
  } catch {
    return false
  }
})()

const memoryStore = new Map<string, string>()

const getItem = (key: string): string | null =>
  hasLocalStorage ? window.localStorage.getItem(key) : (memoryStore.get(key) ?? null)

const setItem = (key: string, value: string) => {
  if (hasLocalStorage) window.localStorage.setItem(key, value)
  else memoryStore.set(key, value)
}

const removeItem = (key: string) => {
  if (hasLocalStorage) window.localStorage.removeItem(key)
  else memoryStore.delete(key)
}

const broadcastChange = (key: string) => {
  try {
    const event = new CustomEvent(DISMISSALS_CHANGED_EVENT, { detail: { key } })
    window.dispatchEvent(event)
  } catch {
    // no-op if CustomEvent is unavailable
  }
}

export const storageKeyFor = (id: string, scope: string = 'global') => `${NAMESPACE}:${scope}:${id}`

const safeParse = (raw: string | null): DismissalRecord | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as DismissalRecord
    return parsed && parsed.id ? parsed : null
  } catch {
    return null
  }
}

const toExpiresAt = (opts?: DismissOptions): number | undefined => {
  if (!opts) return undefined
  if (opts.expiresAt instanceof Date) return opts.expiresAt.getTime()
  if (typeof opts.expiresAt === 'number') return opts.expiresAt
  if (typeof opts.ttlMs === 'number') return Date.now() + Math.max(0, opts.ttlMs)
  if (typeof opts.ttlDays === 'number') return Date.now() + Math.max(0, opts.ttlDays) * 24 * 60 * 60 * 1000
  return undefined
}

const isExpired = (rec: DismissalRecord | null) => !!rec?.expiresAt && Date.now() > (rec.expiresAt as number)

export const DismissalStore = {
  isDismissed(id: string, scope: string = 'global'): boolean {
    const key = storageKeyFor(id, scope)
    const rec = safeParse(getItem(key))
    if (!rec) return false
    if (isExpired(rec)) {
      removeItem(key)
      return false
    }
    return true
  },

  get(id: string, scope: string = 'global'): DismissalRecord | null {
    const key = storageKeyFor(id, scope)
    const rec = safeParse(getItem(key))
    if (!rec) return null
    if (isExpired(rec)) {
      removeItem(key)
      return null
    }
    return rec
  },

  dismiss(id: string, opts?: DismissOptions, scope: string = 'global'): DismissalRecord {
    const record: DismissalRecord = {
      id,
      scope,
      dismissedAt: Date.now(),
      expiresAt: toExpiresAt(opts),
      version: 1,
    }
    const key = storageKeyFor(id, scope)
    setItem(key, JSON.stringify(record))
    broadcastChange(key)
    return record
  },

  reset(id: string, scope: string = 'global'): void {
    const key = storageKeyFor(id, scope)
    removeItem(key)
    broadcastChange(key)
  },
}

export default DismissalStore
